import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { isMobileAppRuntime } from 'utils';

const EMPTY_REQUEST = {
  operation: 'waiting',
  urls: [],
  error: '',
};

const getActivityDetails = (flinkPlay, game) => {
  const current = game?.currentActivity || flinkPlay?.activity || {};
  const activity = current?.activity || current;
  const template = current?.template || {};

  return {
    id: activity?._id || current?._id || '',
    name:
      activity?.activityName ||
      activity?.name ||
      current?.activityName ||
      current?.name ||
      '',
    template:
      template?.alias ||
      template?.activityTemplateName ||
      activity?.templateAlias ||
      activity?.templateId ||
      '',
  };
};

const resolveContext = ({ surface, flinkPlay, game }) => {
  const help = flinkPlay?.help;
  if (help) {
    return help.audioDebugContext ||
      `Help dialog | String #${help.stringNumber || 'not set'}${help.activityGroupId ? ` | Activity Group ${help.activityGroupId}` : ''}`;
  }

  const activity = getActivityDetails(flinkPlay, game);
  if (game?.isActivityHelpOpened) {
    return `Activity Help | ${activity.name || 'Unnamed activity'}${activity.id ? ` | ID ${activity.id}` : ''}${activity.template ? ` | Template ${activity.template}` : ''}`;
  }
  if (game?.isTeamActivityHelpOpened) {
    return `Activity Team Help | ${activity.name || 'Unnamed activity'}${activity.id ? ` | ID ${activity.id}` : ''}${activity.template ? ` | Template ${activity.template}` : ''}`;
  }
  if (flinkPlay?.activity || game?.currentActivity) {
    return `Activity | ${activity.name || 'Unnamed activity'}${activity.id ? ` | ID ${activity.id}` : ''}${activity.template ? ` | Template ${activity.template}` : ''}`;
  }
  if (flinkPlay?.currentMenu) {
    const menu = flinkPlay.currentMenu;
    return `Menu | ${menu.menuName || menu.name || menu._id || 'Unnamed menu'}`;
  }
  if (flinkPlay?.currentActivityGroup) {
    return `Activity Group | ${flinkPlay.currentActivityGroup}`;
  }
  return surface;
};

const MobileAudioDebugPanel = ({
  surface,
  enabled,
  audioSrc,
  isPlaying,
  flinkPlay,
  game,
}) => {
  const [request, setRequest] = useState(EMPTY_REQUEST);
  const debugEnabled = enabled;

  useEffect(() => {
    if (!debugEnabled) return undefined;

    const handleRequest = (event) => {
      const detail = event?.detail || {};
      setRequest({
        operation: detail.operation || 'requested',
        urls: Array.isArray(detail.urls) ? detail.urls : [],
        error: '',
      });
    };
    const handleEnded = (event) => {
      const detail = event?.detail || {};
      const urls = (Array.isArray(detail.src) ? detail.src : [detail.src]).filter(Boolean);
      setRequest((current) => ({
        operation: detail.errorEvent || detail.message ? 'error' : 'ended',
        urls: urls.length ? urls : current.urls,
        error: detail.message || detail.errorEvent?.message || String(detail.errorEvent || ''),
      }));
    };

    window.addEventListener('flcMobileAudioDebug', handleRequest);
    document.addEventListener('audioEnded', handleEnded);
    return () => {
      window.removeEventListener('flcMobileAudioDebug', handleRequest);
      document.removeEventListener('audioEnded', handleEnded);
    };
  }, [debugEnabled]);

  const context = useMemo(
    () => resolveContext({ surface, flinkPlay, game }),
    [surface, flinkPlay, game]
  );
  const urls = request.urls.length
    ? request.urls
    : (audioSrc ? [audioSrc] : []);
  const status = request.error
    ? 'error'
    : (isPlaying && audioSrc ? 'playing' : request.operation);

  if (!debugEnabled || !isMobileAppRuntime()) return null;

  return (
    <div style={{
      position: 'fixed',
      left: 8,
      right: 8,
      bottom: 'max(8px, env(safe-area-inset-bottom))',
      zIndex: 100000,
      padding: '6px 8px',
      border: '1px solid #c62828',
      borderRadius: 4,
      background: 'rgba(255,255,255,.96)',
      color: '#c62828',
      fontFamily: 'monospace',
      fontSize: 11,
      lineHeight: 1.35,
      overflowWrap: 'anywhere',
      userSelect: 'text',
    }}>
      <div><strong>Audio context:</strong> {context}</div>
      <div><strong>Operation/status:</strong> {request.operation} / {status}</div>
      <div>
        <strong>Requested audio URL:</strong>{' '}
        {urls.length ? urls.join(' | ') : 'No audio request has been made on this screen yet.'}
      </div>
      {request.error && <div><strong>Error:</strong> {request.error}</div>}
    </div>
  );
};

MobileAudioDebugPanel.propTypes = {
  surface: PropTypes.string,
  enabled: PropTypes.bool,
  audioSrc: PropTypes.string,
  isPlaying: PropTypes.bool,
  flinkPlay: PropTypes.object,
  game: PropTypes.object,
};

MobileAudioDebugPanel.defaultProps = {
  surface: 'Mobile screen',
};

const mapStateToProps = ({ common, audio, flinkPlay, game }) => ({
  enabled:
    common.config?.customContentSettings?.mobileWrapper?.audioDebugMode === true,
  audioSrc: audio?.audioSrc || '',
  isPlaying: audio?.isPlaying === true,
  flinkPlay,
  game,
});

export default connect(mapStateToProps)(MobileAudioDebugPanel);
